import { ReactNode, useState } from 'react';
import { Menu, X } from 'lucide-react';
import Sidebar from './Sidebar';
import { AppType, PageType } from '../../hooks/useNavigation';
import { Section } from '../../hooks/useSections';

interface PublicLayoutProps {
  children: ReactNode;
  currentApp: AppType;
  currentPage: PageType;
  mainSections: Section[];
  hiSections: Section[];
  currentSectionId: string | null;
  currentTopicId: string | null;
  goHome: () => void;
  goToSection: (id: string) => void;
  goToTopic: (sectionId: string, topicId: string) => void;
  goHIHome: () => void;
  goToHISection: (id: string) => void;
  goToHITopic: (sectionId: string, topicId: string) => void;
}

export default function PublicLayout({
  children,
  currentApp,
  currentPage,
  mainSections,
  hiSections,
  currentSectionId,
  currentTopicId,
  goHome,
  goToSection,
  goToTopic,
  goHIHome,
  goToHISection,
  goToHITopic,
}: PublicLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Wrap nav functions to close mobile menu on navigation
  const wrapNav = <T extends (...args: any[]) => void>(fn: T): T => {
    return ((...args: any[]) => {
      fn(...args);
      setMobileMenuOpen(false);
    }) as T;
  };

  return (
    <div className="flex min-h-screen bg-surface-light font-sans">
      {/* Main content area */}
      <main className="flex-1 flex flex-col overflow-auto">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-content-border">
          <button onClick={goHome} className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-navy to-oak-green rounded-lg flex items-center justify-center">
              <span className="text-white text-sm">🌳</span>
            </div>
            <span className="font-bold text-navy text-sm">OAK Library</span>
          </button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-surface-light rounded-lg"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <div className="flex-1 py-8 px-4 sm:px-8 lg:px-12">{children}</div>
      </main>

      {/* Desktop Sidebar (hidden on mobile) */}
      <div className="hidden lg:block">
        <Sidebar
          currentApp={currentApp}
          currentPage={currentPage}
          mainSections={mainSections}
          hiSections={hiSections}
          currentSectionId={currentSectionId}
          currentTopicId={currentTopicId}
          goHome={goHome}
          goToSection={goToSection}
          goToTopic={goToTopic}
          goHIHome={goHIHome}
          goToHISection={goToHISection}
          goToHITopic={goToHITopic}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/30 z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="lg:hidden fixed right-0 top-0 bottom-0 z-50 w-[280px] shadow-xl">
            <Sidebar
              currentApp={currentApp}
              currentPage={currentPage}
              mainSections={mainSections}
              hiSections={hiSections}
              currentSectionId={currentSectionId}
              currentTopicId={currentTopicId}
              goHome={wrapNav(goHome)}
              goToSection={wrapNav(goToSection)}
              goToTopic={wrapNav(goToTopic)}
              goHIHome={wrapNav(goHIHome)}
              goToHISection={wrapNav(goToHISection)}
              goToHITopic={wrapNav(goToHITopic)}
            />
          </div>
        </>
      )}
    </div>
  );
}
